"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  IELTS_ANCHORS,
  IELTS_SECTIONS,
  type IeltsRawModule,
  type IeltsSection,
  ieltsRawPlacement,
  ieltsRounding,
  ieltsWithMissing,
  isIeltsBand,
  isRawScore,
} from "@/lib/tool-kit/exam-scores/ielts";

/** English strings. A translated content file overrides these through `ui`. */
export const IELTS_BAND_SCORE_CALCULATOR_STRINGS = {
  bandsHeading: "Your section band scores",
  bandsHint:
    "Bands run from 0 to 9 in half-band steps. Leave one section blank to see what it would need to be.",
  sections: {
    listening: "Listening",
    reading: "Reading",
    writing: "Writing",
    speaking: "Speaking",
  },
  sectionLabel: "{section} band (0–9)",
  results: "Overall band",
  overall: "Overall band score",
  average: "Average of the four sections",
  working: "({sum}) ÷ 4 = {average} → {overall}",
  roundedUp: "Rounded up to the nearest half band, as IELTS's rule requires.",
  roundedDown:
    "Rounded down to the nearest half band, as IELTS's rule requires.",
  exact: "The average is already a whole or half band, so nothing is rounded.",
  invalid:
    "{section} must be a band from 0 to 9 in steps of 0.5 — it is left out until it is corrected.",
  enterBands: "Enter at least three valid section bands to see a result.",
  missingHeading: "What your {section} band needs to be",
  missingMessage:
    "With {section} still to come, this is the lowest {section} band that gives each overall band.",
  colBand: "Lowest band needed",
  colOverall: "Overall band",
  rawHeading: "Listening and Reading marks (out of 40)",
  rawHint:
    "IELTS publishes the average marks for only a few bands, and they vary slightly between test versions. Use them as a guide, not a conversion.",
  moduleLabel: "Test part",
  modules: {
    listening: "Listening",
    "academic-reading": "Academic Reading",
    "general-reading": "General Training Reading",
  },
  marksLabel: "Your marks out of 40",
  rawInvalid: "Enter a whole number of marks from 0 to 40.",
  rawReached:
    "{marks} marks is at or above the published average for band {band} ({listed} marks).",
  rawBelow:
    "{marks} marks is below the lowest average IELTS publishes (band {band}, {listed} marks).",
  rawNext:
    "one: {needed} more mark reaches the published average for band {band}.|other: {needed} more marks reach the published average for band {band}.",
  rawTop: "That is at or above the highest published average, band {band}.",
  anchorsHeading: "Average marks IELTS publishes",
  colAnchorBand: "Band",
  colMarks: "Average marks out of 40",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "IELTS overall band {overall} (average {average})",
} as const;

type Strings = typeof IELTS_BAND_SCORE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

const BAND_FORMAT = { minimumFractionDigits: 1, maximumFractionDigits: 3 };
const MODULES: readonly IeltsRawModule[] = [
  "listening",
  "academic-reading",
  "general-reading",
];

type Entries = Record<IeltsSection, string>;
const INITIAL: Entries = {
  listening: "6.5",
  reading: "6.5",
  writing: "5.5",
  speaking: "6",
};

interface ReadBands {
  known: number[];
  blank: IeltsSection[];
  invalid: IeltsSection[];
}

function readBands(entries: Entries): ReadBands {
  const out: ReadBands = { known: [], blank: [], invalid: [] };
  for (const section of IELTS_SECTIONS) {
    const raw = entries[section].trim();
    const value = parseNumberInput(raw);
    if (raw === "") {
      out.blank.push(section);
    } else if (isIeltsBand(value)) {
      out.known.push(value);
    } else {
      out.invalid.push(section);
    }
  }
  return out;
}

function OverallPanel({
  values,
  strings,
  fmt,
}: {
  values: number[];
  strings: Strings;
  fmt: Format;
}) {
  const { average, overall, kind } = ieltsRounding(values);
  const note = {
    up: strings.roundedUp,
    down: strings.roundedDown,
    exact: strings.exact,
  }[kind];
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.overall}
          value={fmt(overall, BAND_FORMAT)}
        />
        <StatCard label={strings.average} value={fmt(average, BAND_FORMAT)} />
      </StatGrid>
      <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
        {t(strings.working, {
          sum: values.map((value) => fmt(value, BAND_FORMAT)).join(" + "),
          average: fmt(average, BAND_FORMAT),
          overall: fmt(overall, BAND_FORMAT),
        })}
      </p>
      <p className="text-muted-foreground text-sm">{note}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, {
          overall: fmt(overall, BAND_FORMAT),
          average: fmt(average, BAND_FORMAT),
        })}
      />
    </>
  );
}

function MissingPanel({
  known,
  section,
  strings,
  fmt,
}: {
  known: number[];
  section: string;
  strings: Strings;
  fmt: Format;
}) {
  const rows = ieltsWithMissing(known).filter(
    (row, index, all) => index === 0 || row.overall !== all[index - 1]?.overall
  );
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{t(strings.missingHeading, { section })}</h3>
      <p className="text-muted-foreground text-sm">
        {t(strings.missingMessage, { section })}
      </p>
      <DataTable
        head={[strings.colBand, strings.colOverall]}
        rows={rows.map((row) => [
          fmt(row.band, BAND_FORMAT),
          fmt(row.overall, BAND_FORMAT),
        ])}
      />
    </div>
  );
}

function placementText(
  module: IeltsRawModule,
  score: number,
  strings: Strings,
  fmt: Format,
  locale: string
): string {
  const placement = ieltsRawPlacement(module, score);
  const first = IELTS_ANCHORS[module][0];
  const lines: string[] = [];
  if (placement.reached) {
    lines.push(
      t(strings.rawReached, {
        marks: fmt(score),
        band: fmt(placement.reached.band),
        listed: fmt(placement.reached.marks),
      })
    );
  } else if (first) {
    lines.push(
      t(strings.rawBelow, {
        marks: fmt(score),
        band: fmt(first.band),
        listed: fmt(first.marks),
      })
    );
  }
  if (placement.next && placement.toNext !== null) {
    lines.push(
      tPlural(strings.rawNext, locale, placement.toNext, {
        needed: fmt(placement.toNext),
        band: fmt(placement.next.band),
      })
    );
  } else if (placement.reached) {
    lines.push(t(strings.rawTop, { band: fmt(placement.reached.band) }));
  }
  return lines.join(" ");
}

function RawPanel({
  strings,
  fmt,
  locale,
}: {
  strings: Strings;
  fmt: Format;
  locale: string;
}) {
  const [module, setModule] = useState<IeltsRawModule>("listening");
  const [marks, setMarks] = useState("");
  const value = parseNumberInput(marks);
  let message: string | null = null;
  if (marks.trim() !== "") {
    message = isRawScore(value)
      ? placementText(module, value, strings, fmt, locale)
      : strings.rawInvalid;
  }
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{strings.rawHeading}</h3>
        <p className="text-muted-foreground text-sm">{strings.rawHint}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ielts-raw-module"
          label={strings.moduleLabel}
          onChange={setModule}
          options={MODULES.map((id) => ({
            value: id,
            label: strings.modules[id],
          }))}
          value={module}
        />
        <TextField
          id="ielts-raw-marks"
          label={strings.marksLabel}
          numeric
          onChange={setMarks}
          value={marks}
        />
      </div>
      <div aria-live="polite">
        {message ? <Notice>{message}</Notice> : null}
      </div>
      <DataTable
        caption={`${strings.anchorsHeading} — ${strings.modules[module]}`}
        head={[strings.colAnchorBand, strings.colMarks]}
        rows={IELTS_ANCHORS[module].map((anchor) => [
          fmt(anchor.band),
          fmt(anchor.marks),
        ])}
      />
    </section>
  );
}

export default function IeltsBandScoreCalculatorWidget() {
  const strings = useToolUi(IELTS_BAND_SCORE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [entries, setEntries] = useState<Entries>(INITIAL);
  const read = readBands(entries);
  const missing =
    read.known.length === 3 && read.blank.length === 1 ? read.blank[0] : null;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.bandsHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.bandsHint}</p>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {IELTS_SECTIONS.map((section) => (
          <TextField
            id={`ielts-${section}`}
            key={section}
            label={t(strings.sectionLabel, {
              section: strings.sections[section],
            })}
            numeric
            onChange={(value) =>
              setEntries((all) => ({ ...all, [section]: value }))
            }
            value={entries[section]}
          />
        ))}
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {read.invalid.map((section) => (
          <Notice key={section}>
            {t(strings.invalid, { section: strings.sections[section] })}
          </Notice>
        ))}
        {read.known.length === 4 ? (
          <OverallPanel fmt={fmt} strings={strings} values={read.known} />
        ) : null}
        {missing ? (
          <MissingPanel
            fmt={fmt}
            known={read.known}
            section={strings.sections[missing]}
            strings={strings}
          />
        ) : null}
        {read.known.length < 3 || (read.known.length === 3 && !missing) ? (
          <Notice>{strings.enterBands}</Notice>
        ) : null}
      </section>

      <RawPanel fmt={fmt} locale={locale} strings={strings} />
    </div>
  );
}
